/**
 * Content — what an item needs in order to be RENDERED, and nothing more.
 *
 * Two families of type, deliberately:
 *
 *   *Doc  — the document on disk. Localized: every string is a map of locale to
 *           text. This is what the schema validates and what a host stores.
 *   bare  — the same content resolved to one locale. This is what `app.content`
 *           is, so an asset author writes `item.prompt` rather than
 *           `item.prompt[app.locale]` — one fewer thing for a generator to get
 *           wrong, and one fewer way to index with a locale that isn't present.
 *
 * Nothing here says which answer is correct. That is `scheme.ts`, and the
 * separation is what makes withholding the answer in exam mode a projection of
 * the same content rather than a special case in the asset (D5).
 */

import { pick } from './locale.js';
import type { LocalizedText, Locale } from './locale.js';

export const ITEM_TYPES = [
  'choice',
  'multiChoice',
  'textEntry',
  'ordering',
  'matching',
  'numeric',
] as const;
export type ItemType = (typeof ITEM_TYPES)[number];

/**
 * How many left-hand entries may share one right-hand entry, in a `matching` item.
 *
 * A rendering fact, which is why it is here and not in the scheme: the asset needs it to draw
 * the interaction, and grading does not change — each left is compared to its own answer
 * either way. The scorer never sees this file and does not need to.
 *
 * `many-to-one` is the default because the wrong guess is merely permissive: an item that
 * meant `one-to-one` lets a learner give an answer the activity considers impossible. The
 * other way round, defaulting to `one-to-one`, would make a classification item
 * **unanswerable** — the second animal could not be put in the same group as the first.
 */
export const PAIRING_RULES = ['one-to-one', 'many-to-one'] as const;
export type Pairing = (typeof PAIRING_RULES)[number];

// ---------------------------------------------------------------- on disk

export interface ContentDoc {
  readonly $schema?: string;
  /**
   * Absent — not empty — for an asset with nothing to grade. Absence is how zero
   * items is expressed (D15), which is why this is optional rather than `[]`.
   */
  readonly items?: readonly ItemDoc[];
}

export type ItemDoc =
  | ChoiceItemDoc
  | MultiChoiceItemDoc
  | TextEntryItemDoc
  | OrderingItemDoc
  | MatchingItemDoc
  | NumericItemDoc;

export interface ChoiceItemDoc {
  readonly id: string;
  readonly type: 'choice';
  readonly prompt: LocalizedText;
  readonly choices: readonly ChoiceDoc[];
}

export interface ChoiceDoc {
  readonly id: string;
  readonly text: LocalizedText;
}

/**
 * Pick every one that applies — checkboxes rather than radios.
 *
 * **A separate type from `choice`, and the reason is which file the asset can see.** The
 * interaction differs — a learner may tick two boxes — and the asset must know that in order
 * to draw the control at all. It only ever sees this file (D5), so "how many may be picked"
 * has to live here. Putting it in the scheme would make the asset unable to render itself in
 * exam mode, where the scheme is withheld.
 *
 * Identical in shape to `choice` on purpose, exactly as `ordering` is: the same `choices`
 * list, so an asset renders either from the same data. Unlike `matching`'s `pairing`, this is
 * not a flag on an existing type, because the response shape, the scorer and the scheme's
 * `correct` all differ — one id versus a set of them. That is [D40](../../DECISIONS.md)'s own
 * test for when a second type is warranted rather than a second mode.
 *
 * **How many are correct is not here.** It is `scheme.correct.length`, and stating it in the
 * content would narrow the answer for a learner reading the published file — "exactly two of
 * these five" is a large part of the answer. If the activity wants to say "choose two", the
 * prompt says it, and then the author has chosen to reveal it.
 */
export interface MultiChoiceItemDoc {
  readonly id: string;
  readonly type: 'multiChoice';
  readonly prompt: LocalizedText;
  readonly choices: readonly ChoiceDoc[];
}

export interface TextEntryItemDoc {
  readonly id: string;
  readonly type: 'textEntry';
  readonly prompt: LocalizedText;
  readonly input?: 'short' | 'long';
}

/**
 * Type a number, into one entry field or several. The scheme says which numbers count.
 *
 * **A separate type from `textEntry`, and the reason is the comparison, not the control.** A
 * number is not a string that happens to hold digits: `2.50` and `2.5` are the same answer and
 * `textEntry` marks one of them wrong, because its comparison is string equality after
 * normalization and no accept list can enumerate an interval. That is D40's test for a second
 * type — the response shape, the scorer and the scheme's answer all differ.
 *
 * **Named for the interaction, not the grading rule.** `numericRange` was considered and
 * rejected: this file is published, the scheme is not, and a type name that states how an item
 * is marked puts a grading fact on the wrong side of the D5 line. It would also misdescribe an
 * exact answer, which is `min === max`. The existing names work the same way — `choice` is not
 * `choiceSingleCorrect`, `textEntry` is not `textEntryAcceptList`.
 */
export interface NumericItemDoc {
  readonly id: string;
  readonly type: 'numeric';
  readonly prompt: LocalizedText;
  /**
   * How many entry fields to draw. Defaults to 1, which is the ordinary case; more than one is
   * "give three numbers that…", where each field is graded against the same rule.
   *
   * **Here rather than only in the scheme**, for the reason `multiChoice` declares checkboxes
   * here: the asset only ever sees this file (D5) and must still render itself in exam mode,
   * where the scheme is withheld entirely. A field count the asset could not read would be an
   * item it could not draw.
   *
   * The *bounds* are not here, and neither is `distinct`. If the activity wants to say "four
   * different numbers between 2.2 and 3.1", the prompt says it, and the author has then chosen
   * to reveal it — the same arrangement as `multiChoice`, where "choose two" is a prompt and
   * never a field.
   */
  readonly fields?: number;
}

/**
 * Put these in the right order. Drag-and-drop, a sortable list, up/down buttons — the
 * interaction is entirely the asset's business, and the reported answer is the sequence
 * of ids.
 *
 * Identical in shape to a choice item on purpose: the same `choices` list, so an asset can
 * render either from the same data and the only difference is what the learner does with
 * it. Added because two of the first real generated assets reached for it — one shipped
 * ungraded, one encoded an ordering as a `textEntry` letter string, which scores
 * all-or-nothing and leaves a gradebook unable to see it was an ordering at all.
 *
 * **`choices` is the order the learner is shown**, which must not be the correct one — a
 * contract rule checks that, because otherwise the answer ships inside the published
 * content.
 */
export interface OrderingItemDoc {
  readonly id: string;
  readonly type: 'ordering';
  readonly prompt: LocalizedText;
  readonly choices: readonly ChoiceDoc[];
}

/**
 * Match each entry on one side to an entry on the other. Connecting lines, drag-and-drop,
 * a pair of lists picked one after the other — the interaction is the asset's business, and
 * the reported answer is a mapping of left id to right id.
 *
 * **Two independent lists, and the pairing is not here.** The old library's Dot2Dot stored
 * `pairs: [{ leftMember, rightMember }]`, so the answer key was the content file: whoever
 * could read the activity's data could read its answers. Two lists plus a mapping in the
 * scheme is the same activity with the answer on the other side of the D5 line.
 *
 * Both shapes of the activity are one type, distinguished by `pairing`:
 *
 *   `one-to-one`   the old library's Dot2Dot. Equal-length sides, each right belonging to
 *                  exactly one left. The learner can reason by elimination, so connecting a
 *                  right that is already taken must move it rather than duplicate it.
 *   `many-to-one`  several lefts to one right — classification — and `right` may be shorter
 *                  than `left`, or longer, the surplus being distractors that match nothing.
 *
 * One type rather than two because everything that carries meaning is identical: the response
 * shape, the scorer, and every validation rule but one. Only the interaction differs.
 */
export interface MatchingItemDoc {
  readonly id: string;
  readonly type: 'matching';
  readonly prompt: LocalizedText;
  readonly left: readonly ChoiceDoc[];
  readonly right: readonly ChoiceDoc[];
  /** Defaults to `many-to-one`. See PAIRING_RULES for why that direction. */
  readonly pairing?: Pairing;
}

// ---------------------------------------------------------------- resolved

export interface Content {
  /**
   * Always present, `[]` when the asset has no items. An asset with no questions
   * can iterate without a null check, which is what makes zero items
   * first-class rather than a degenerate case.
   */
  readonly items: readonly Item[];
}

export type Item =
  | ChoiceItem
  | MultiChoiceItem
  | TextEntryItem
  | OrderingItem
  | MatchingItem
  | NumericItem;

export interface ChoiceItem {
  readonly id: string;
  readonly type: 'choice';
  readonly prompt: string;
  readonly choices: readonly Choice[];
}

export interface Choice {
  readonly id: string;
  readonly text: string;
}

export interface MultiChoiceItem {
  readonly id: string;
  readonly type: 'multiChoice';
  readonly prompt: string;
  readonly choices: readonly Choice[];
}

export interface TextEntryItem {
  readonly id: string;
  readonly type: 'textEntry';
  readonly prompt: string;
  /** Resolved: the schema's default of `short` is applied here, not left absent. */
  readonly input: 'short' | 'long';
}

export interface OrderingItem {
  readonly id: string;
  readonly type: 'ordering';
  readonly prompt: string;
  /** In the order to display, which is deliberately not the correct one. */
  readonly choices: readonly Choice[];
}

export interface NumericItem {
  readonly id: string;
  readonly type: 'numeric';
  readonly prompt: string;
  /** Resolved: the default of 1 is applied here, so an asset always has a number to loop to. */
  readonly fields: number;
}

export interface MatchingItem {
  readonly id: string;
  readonly type: 'matching';
  readonly prompt: string;
  readonly left: readonly Choice[];
  /** Under `many-to-one` this may be longer than `left`; the surplus match nothing. */
  readonly right: readonly Choice[];
  /** Resolved: the default is applied here, so an asset never has to know what it is. */
  readonly pairing: Pairing;
}

// ---------------------------------------------------------------- resolution

/**
 * Pure. `null` in — as when an asset has no content file at all — gives
 * `{ items: [] }`.
 */
export function resolveContent(doc: ContentDoc | null | undefined, locale: Locale): Content {
  const items = (doc?.items ?? []).map((item) => resolveItem(item, locale));
  return { items };
}

export function resolveItem(item: ItemDoc, locale: Locale): Item {
  switch (item.type) {
    case 'choice':
      return {
        id: item.id,
        type: 'choice',
        prompt: pick(item.prompt, locale),
        choices: item.choices.map((choice) => ({
          id: choice.id,
          text: pick(choice.text, locale),
        })),
      };
    case 'multiChoice':
      return {
        id: item.id,
        type: 'multiChoice',
        prompt: pick(item.prompt, locale),
        choices: item.choices.map((choice) => ({
          id: choice.id,
          text: pick(choice.text, locale),
        })),
      };
    case 'textEntry':
      return {
        id: item.id,
        type: 'textEntry',
        prompt: pick(item.prompt, locale),
        input: item.input ?? 'short',
      };
    case 'ordering':
      return {
        id: item.id,
        type: 'ordering',
        prompt: pick(item.prompt, locale),
        choices: item.choices.map((choice) => ({
          id: choice.id,
          text: pick(choice.text, locale),
        })),
      };
    case 'matching':
      return {
        id: item.id,
        type: 'matching',
        prompt: pick(item.prompt, locale),
        left: item.left.map((choice) => ({ id: choice.id, text: pick(choice.text, locale) })),
        right: item.right.map((choice) => ({ id: choice.id, text: pick(choice.text, locale) })),
        pairing: item.pairing ?? 'many-to-one',
      };
    case 'numeric':
      return {
        id: item.id,
        type: 'numeric',
        prompt: pick(item.prompt, locale),
        fields: item.fields ?? 1,
      };
  }
}
